
export class RhythmState {
    // Phrasing State (Sustain implementation)
    private touchesSinceLastChordTrigger: number = 100; // Force start
    private nextChordTriggerGap: number = 0;

    // Pacing State (Chord Progression speed)
    private notesSinceLastChordChange: number = 0;
    private currentChordDuration: number = 4;

    constructor() {
        this.reset();
    }

    public reset() {
        this.touchesSinceLastChordTrigger = 100;
        this.nextChordTriggerGap = 0;
        this.notesSinceLastChordChange = 0;
        this.currentChordDuration = Math.floor(Math.random() * 5) + 4;
    }

    public incrementTouch() {
        this.touchesSinceLastChordTrigger++;
    }

    /**
     * Determines if a chord should be triggered for this touch.
     * Returns the decision and an optional duration for sustained chords.
     */
    public shouldTriggerChord(textureMode: 'melodic' | 'chordal' | 'mixed', dynamics?: { velocity: number; yPos: number }): { shouldPlay: boolean, duration?: number } {
        let playChord = false;

        // 1. Force Logic (Y-Axis) - SWAPPED (Bottom = Heavy/Chords, Top = Light/Melody)
        // Visual Layout (HowToPlay.tsx): Top ~29%, Middle ~41%, Bottom ~29%
        if (dynamics) {
            // Bottom 30% -> Force Chords (Harmony & Earth)
            if (dynamics.yPos > 0.7) playChord = true;
            // Top 30% -> Force Single Notes (Melody & Air)
            else if (dynamics.yPos < 0.3) playChord = false;
        }

        // 2. Mode Logic
        if (textureMode === 'melodic') playChord = false;
        if (textureMode === 'chordal' && !dynamics) playChord = true;

        // 3. Dynamic Gap Logic (The "Phrasing" System) - Middle Zone (Flow)
        // Now accurately covers the 0.3 - 0.7 range without dead zones
        if (!playChord && textureMode !== 'melodic' && (!dynamics || (dynamics.yPos >= 0.3 && dynamics.yPos <= 0.7))) {
            if (this.touchesSinceLastChordTrigger >= this.nextChordTriggerGap) {
                playChord = true;
            }
        }

        if (playChord) {
            // Reset Counter
            this.touchesSinceLastChordTrigger = 0;

            let duration: number | undefined = undefined;

            // DECIDE NEXT GAP (Phrasing)
            const roll = Math.random();
            if (roll < 0.55) {
                // SUSTAIN PHRASE (55%) - Long chords
                this.nextChordTriggerGap = Math.floor(Math.random() * 5) + 4;
                duration = 1.8; // Was 3.0
            } else if (roll < 0.90) {
                // MEDIUM PHRASE (35%)
                this.nextChordTriggerGap = Math.floor(Math.random() * 2) + 2;
            } else {
                // RHYTHMIC PHRASE (10%) - Faster changes
                // Increase minimum gap to 2 to prevent "machine gun" chords
                this.nextChordTriggerGap = 2;
            }

            // CALL & RESPONSE (Feature 5)
            // If user is playing at high density, simplify backing
            // If user is idle, complicate backing
            const touchRate = this.getTouchRate();
            if (touchRate > 2.0) {
                // High density: User is "calling" - reduce accompaniment
                duration = Math.max(1.5, duration * 0.5);
            } else if (touchRate < 0.5) {
                // Low density: Respond with fuller comping
                this.nextChordTriggerGap = Math.max(1, this.nextChordTriggerGap - 1);
            }

            return { shouldPlay: true, duration };
        }

        return { shouldPlay: false };
    }

    /**
     * Feature 5: Get recent touch rate (touches per second approx)
     */
    private getTouchRate(): number {
        // Simple approximation based on touchesSinceLastChordTrigger and nextChordTriggerGap
        // Assume 1 gap unit ~ 0.5 seconds
        if (this.nextChordTriggerGap === 0) return 0;
        return this.touchesSinceLastChordTrigger / (this.nextChordTriggerGap * 0.5);
    }

    /**
     * Checks if we should advance to the next chord in the progression.
     * Automatically updates the internal counter.
     */
    /**
     * Checks if we should advance to the next chord in the progression.
     * Automatically updates the internal counter.
     */
    public shouldAdvanceProgression(force: boolean = false, targetDuration: number = 8): boolean {
        this.notesSinceLastChordChange++;

        // Use targetDuration to determine if we exceeded time
        if (force || this.notesSinceLastChordChange >= this.currentChordDuration) {
            this.notesSinceLastChordChange = 0;
            // Set next duration based on the PASSED target (which comes from StructureEngine)
            // Add slight variance (+/- 1)
            const variance = Math.floor(Math.random() * 3) - 1;
            this.currentChordDuration = Math.max(2, targetDuration + variance);
            return true;
        }
        return false;
    }

    /**
     * V5: Helper for Lookahead (Secondary Dominants)
     * Returns 0.0 to 1.0 indicating how close we are to the next chord change.
     */
    public getProgressionProgress(): number {
        if (this.currentChordDuration === 0) return 1;
        return this.notesSinceLastChordChange / this.currentChordDuration;
    }
}
